import { type AppState, Auth0Provider } from "@auth0/auth0-react";
import { useNavigate, useLocation } from "react-router";
import { useState, useEffect, type ReactNode } from "react";

interface LocationState {
  returnTo?: string;
}

export function Auth0ProviderWithNavigate({
  children,
}: {
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const redirectUri = import.meta.env.VITE_AUTH0_CALLBACK_URL;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

  const onRedirectCallback = (appState: AppState | undefined) => {
    const locationState = location.state as LocationState | null;
    const returnTo =
      appState?.returnTo ?? locationState?.returnTo ?? "/dispatch";
    void navigate(returnTo);
  };

  // Return children while not mounted to prevent hydration mismatch
  if (!isMounted) {
    return <>{children}</>;
  }

  if (!(domain && clientId && redirectUri)) {
    return <>{children}</>;
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: redirectUri,
        audience: audience,
        scope: "openid profile email",
      }}
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
}
